import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { CultureKeyType } from '@constants/app.constants';
import {
  ADULT_MAX_COUNT,
  ADULT_MIN_COUNT,
  CHILD_MAX_COUNT,
  CHILD_MIN_COUNT,
  INFANT_MIN_COUNT,
  MAX_TRAVELER_COUNT,
} from '@constants/home.constants';
import { LetDirective } from '@directives/app-let.directive';
import { SearchWidgetCopy } from '@models/cms/search-widget.model';
import { ModalService } from '@services/modal/modal.service';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ConfirmationMessageModalComponent } from '../confirmation-message-modal/confirmation-message-modal.component';
import { take } from 'rxjs';
import {
  TravelerPickerModalSubmitButtonComponent
} from "@components/modals/traveler-picker-modal/components/traveler-picker-modal-submit-button/traveler-picker-modal-submit-button.component";

@Component({
  selector: 'app-traveler-picker-modal',
  standalone: true,
  imports: [CommonModule, LetDirective, ReactiveFormsModule, TravelerPickerModalSubmitButtonComponent],
  templateUrl: './traveler-picker-modal.component.html',
  styleUrls: ['./traveler-picker-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(
        /* istanbul ignore next */
        () => TravelerPickerModalComponent,
      ),
      multi: true,
    },
  ],
})
export class TravelerPickerModalComponent implements ControlValueAccessor {
  @Input() cmsSearchWidgetCopy: SearchWidgetCopy;
  @Output() response = new EventEmitter<boolean>();

  private modalService = inject(ModalService);

  readonly maxTravelerCount = MAX_TRAVELER_COUNT;

  travelerPickerVisible = false;
  routeSelectorBorderRadius = 'borderRadiusdefault';

  value: unknown;
  changed: (value: string) => void;
  touched: () => void;

  culture: CultureKeyType;
  travelersFormGroup!: FormGroup;

  get adultCountCtrl(): FormControl<number> {
    return <FormControl<number>>this.travelersFormGroup.get('adultCount');
  }

  get adultCount(): number {
    return this.adultCountCtrl?.value ?? 0;
  }

  get youthCountCtrl(): FormControl<number> {
    return <FormControl<number>>this.travelersFormGroup.get('youthCount');
  }

  get youthCount(): number {
    return this.youthCountCtrl?.value ?? 0;
  }

  get childCountCtrl(): FormControl<number> {
    return <FormControl<number>>this.travelersFormGroup.get('childCount');
  }

  get childCount(): number {
    return this.childCountCtrl?.value ?? 0;
  }

  get infantCountCtrl(): FormControl<number> {
    return <FormControl<number>>this.travelersFormGroup.get('infantCount');
  }

  get infantCount(): number {
    return this.infantCountCtrl?.value ?? 0;
  }

  get infantInOwnSeatCountCtrl(): FormControl<number> {
    return <FormControl<number>>(
      this.travelersFormGroup.get('infantInOwnSeatCount')
    );
  }

  get infantInOwnSeatCount(): number {
    return this.infantInOwnSeatCountCtrl?.value ?? 0;
  }

  constructor(
    private cd: ChangeDetectorRef,
    public bsModalRef: BsModalRef,
  ) {}

  totalTravelerCount(): number {
    this.cd.markForCheck();
    return (
      this.adultCount +
      this.childCount +
      this.infantCount +
      this.youthCount +
      this.infantInOwnSeatCount
    );
  }

  totalChildCount(): number {
    this.cd.markForCheck();
    return this.childCount + this.infantInOwnSeatCount + this.youthCount;
  }

  writeValue(value: unknown): void {
    this.value = value;
  }

  registerOnChange(fn: () => void): void {
    this.changed = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.touched = fn;
  }

  //TODO: Create a directive with @HostListener document click and move the outside click logic there.
  onTravelerListClick(id: string): void {
    if (id === '') {
      this.travelerPickerVisible = false;
      this.routeSelectorBorderRadius = 'borderRadiusDefault';
    }
    if (id === 'travelerList') {
      this.travelerPickerVisible = !this.travelerPickerVisible;
      this.routeSelectorBorderRadius = !this.travelerPickerVisible
        ? 'borderRadiusDefault'
        : 'borderRadiusBottomOff';
    }
  }

  onAdultAdd(): void {
    if (
      this.adultCount >= ADULT_MAX_COUNT ||
      this.totalTravelerCount() >= MAX_TRAVELER_COUNT
    ) {
      return;
    }
    this.adultCountCtrl?.setValue(this.adultCount + 1);
  }

  onAdultRemove(): void {
    if (this.adultCount <= ADULT_MIN_COUNT) {
      return;
    }
    this.adultCountCtrl?.setValue(this.adultCount - 1);
    // Infant count should not be greater than adult count.
    if (this.infantCount > this.adultCount) {
      this.infantCountCtrl.setValue(this.adultCount);
    }
  }

  onYouthAdd(): void {
    if (
      this.totalChildCount() >= CHILD_MAX_COUNT ||
      this.totalTravelerCount() >= MAX_TRAVELER_COUNT
    ) {
      return;
    }
    this.youthCountCtrl?.setValue(this.youthCount + 1);
  }

  onYouthRemove(): void {
    if (this.totalChildCount() <= CHILD_MIN_COUNT) {
      return;
    }
    this.youthCountCtrl?.setValue(this.youthCount - 1);
  }

  onChildAdd(): void {
    if (
      this.totalChildCount() >= CHILD_MAX_COUNT ||
      this.totalTravelerCount() >= MAX_TRAVELER_COUNT
    ) {
      return;
    }
    this.childCountCtrl?.setValue(this.childCount + 1);
  }

  onChildRemove(): void {
    if (this.totalChildCount() <= CHILD_MIN_COUNT) {
      return;
    }
    this.childCountCtrl?.setValue(this.childCount - 1);
  }

  onInfantAdd(): void {
    if (
      this.infantCount >= this.adultCount ||
      this.totalTravelerCount() >= MAX_TRAVELER_COUNT
    ) {
      return;
    }
    this.infantCountCtrl?.setValue(this.infantCount + 1);
  }

  onInfantRemove(): void {
    if (this.infantCount <= INFANT_MIN_COUNT) {
      return;
    }
    this.infantCountCtrl?.setValue(this.infantCount - 1);
  }

  onInfantInOwnSeatAdd(): void {
    if (
      this.totalChildCount() >= CHILD_MAX_COUNT ||
      this.totalTravelerCount() >= MAX_TRAVELER_COUNT
    ) {
      return;
    }
    this.infantInOwnSeatCountCtrl?.setValue(this.infantInOwnSeatCount + 1);
  }

  onInfantInOwnSeatRemove(): void {
    if (this.totalChildCount() <= CHILD_MIN_COUNT) {
      return;
    }
    this.infantInOwnSeatCountCtrl?.setValue(this.infantInOwnSeatCount - 1);
  }

  onDoneClick(): void {
    this.travelerPickerVisible = false;
    this.routeSelectorBorderRadius = 'borderRadiusdefault';
  }

  onNextClick(): void {
    if (this.totalTravelerCount() <= 0) {
      this.response.emit(false);
      this.bsModalRef.hide();
    }

    const youthAlone = this.youthCount > 0 && this.adultCount <= 0;
    if (youthAlone) {
      const confirmationRef = this.modalService.show(
        ConfirmationMessageModalComponent,
        {
          class: 'modal-dialog-centered md-confirmation',
          initialState: {
            confirmBtnText: this.cmsSearchWidgetCopy.agedEligibleConfirmation,
            declineBtnText: this.cmsSearchWidgetCopy.goBack,
            messageText: this.cmsSearchWidgetCopy.agedEligibleMessage
          },
        },
      );
      confirmationRef.content?.response.pipe(take(1)).subscribe((response) => {
        if (response) {
          this.response.emit(true);
          this.bsModalRef.hide();
        }
        confirmationRef.hide();
      });
    } else {
      this.response.emit(true);
      this.bsModalRef.hide();
    }
  }
}
